import type { ClientCancelReason } from '@heidi/schema/ml-scribe';

import type { ApprovalContext } from './tool-approval.types';
import type { ToolCallDisplay } from './tool-call-display';

// Public agent-run snapshots intentionally expose only these rebased BE statuses.
// Do not widen this by copying Projects' legacy pending/completed/cancelled union.
export type AgentRunStatus = 'running' | 'closed' | 'failed';

export type AgentRunInputState =
  | 'ready'
  | 'turn_running'
  | 'approval_required'
  | 'closed'
  | 'failed';

// `auto` relaxes ordinary gates but server-side hard floors still apply —
// never suppress approval UI based on this value.
export type ApprovalMode = 'auto' | 'manual';
export type WorkRunTriggerType = 'chat' | 'schedule' | 'event' | 'on_demand';

export const DEFAULT_WORK_CHAT_APPROVAL_MODE = 'manual' satisfies ApprovalMode;

export interface RunUsageSummary {
  total_input_tokens: number;
  total_output_tokens: number;
  total_cache_creation_input_tokens: number;
  total_cache_read_input_tokens: number;
  total_thinking_tokens: number;
}

// A child agent run as summarised on the parent snapshot's `child_runs`
// (shape confirmed against the BE contract). `kind` is the child agent's
// self-declared `AgentConfig.kind` (`"computer_use"` for computer-use
// children); `status` reuses the public run-status union.
export interface ChildRunSummary {
  agent_run_id: string;
  status: AgentRunStatus;
  kind: string | null;
  /** Registered desktop target that owns a device-bound child, when pinned. */
  target_id?: string | null;
  parent_agent_run_id: string;
  created_at?: string | null;
  updated_at?: string | null;
}

// ---- Turn/artifact identity ------------------------------------------------
// Hand-written because the openapi pipeline has not emitted these yet; delete
// them and consume `@heidi/schema/*` once it has. Every field is OPTIONAL so a
// backend without the identity contract still typechecks.

export type RunArtifactPayloadType =
  | 'user_message'
  | 'assistant_message'
  | 'tool_execution'
  | 'compaction_marker'
  | 'turn_termination';

/** How a tool execution ended. `cancelled` is deliberately not an error. */
export type ToolExecutionStatus =
  | 'completed'
  | 'failed'
  | 'denied'
  | 'timed_out'
  | 'cancelled';

/** Distinct from persist status. */
export type ToolTerminalOutcome = 'succeeded' | 'blocked' | 'failed';

export interface RunArtifactPayload {
  type: RunArtifactPayloadType | (string & {});
  /** The artifact's own content — every variant except `tool_execution` carries it. */
  message?: Message;
  /** `turn_termination` only: `cancelled` or `failed`. */
  disposition?: string;
  /** `tool_execution` only: the call this execution terminated. */
  tool_call_id?: string;
  /** `tool_execution` only: a `ToolExecutionStatus` on a backend that stamps it. */
  status?: string;
  terminal_outcome?: string | null;
  /** `turn_termination` only: an allowlisted failure code, when available. */
  reason?: string | null;
  [key: string]: unknown;
}

/** One committed item on the run, addressable independently of `messages[]`. */
export interface RunArtifact {
  artifact_id: string;
  parent_turn_id: string;
  /** Scoped to the parent TURN, not an index into `messages[]`. */
  message_index: number;
  iteration: number | null;
  /** Display only — array order is authoritative; never a sort key. */
  created_at?: string | null;
  payload: RunArtifactPayload;
}

export interface PublicRunSnapshot {
  agent_run_id: string;
  status: AgentRunStatus;
  source_config_id?: string | null;
  source_config_version?: number | null;
  source_schedule_id?: string | null;
  parent_agent_run_id?: string | null;
  input_state: AgentRunInputState;
  can_submit: boolean;
  latest_turn_id?: string | null;
  messages: Message[];
  /** Absent on a backend without the identity contract. */
  artifacts?: RunArtifact[];
  stream_cursor: string | null;
  approvals: ApprovalResponse[];
  approval_mode?: ApprovalMode | null;
  trigger_type?: WorkRunTriggerType | null;
  child_runs?: ChildRunSummary[];
  usage?: RunUsageSummary | null;
  title?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  failure_reason?: string | null;
}

export interface Message {
  role: 'user' | 'assistant';
  blocks: MessageBlock[];
  event_type?: string | null;
  system_generated?: boolean | null;
}

export type MessageBlock =
  | TextBlock
  | ThinkingBlock
  | ToolCallBlock
  | ToolResultBlock
  | AttachmentBlock;

export interface TextBlock {
  kind: 'text';
  text: string;
}

export interface ThinkingBlock {
  kind: 'thinking';
  text: string;
}

export type ToolInputMerge = Record<string, unknown>;

export interface ToolCallBlock {
  kind: 'tool_call';
  id: string;
  name: string;
  input: Record<string, unknown>;
  input_merge?: ToolInputMerge | null;
  provider_metadata?: Record<string, unknown>;
}

export interface ToolResultBlock {
  kind: 'tool_result';
  tool_call_id: string;
  content: unknown;
  is_error: boolean;
  attachments?: AttachmentBlock[];
  provider_metadata?: Record<string, unknown>;
}

export interface AttachmentBlock {
  kind: 'attachment';
  attachment_id: string;
  filename: string;
  mime_type: string;
  attachment_type: string;
  size_bytes?: number | null;
  provider_metadata?: Record<string, unknown>;
  owner_agent_run_id?: string | null;
}

export type ApprovalStatus = 'pending' | 'approved' | 'denied' | 'timed_out';

/** `session` auto-runs the same tool for the rest of the run; `once` is this call only. */
export type ApprovalOverrideScope = 'once' | 'session';

/**
 * What the person did with a proposed call beyond the verdict (ml-scribe
 * `ApprovalResolution`): a registered `kind` and that kind's fields. Each
 * product reads the kinds it owns.
 */
export type ApprovalResolution = { kind: string } & Record<string, unknown>;

export interface ApprovalResponse {
  approval_id: string;
  agent_run_id: string;
  turn_id: string;
  tool_call_id: string;
  tool_name: string;
  tool_input: Record<string, unknown>;
  redacted_tool_input_keys: string[];
  status: ApprovalStatus;
  reason: string;
  modified_input: Record<string, unknown> | null;
  approval_kind: string;
  /** What the person did beyond the verdict, as recorded with it: a registered `kind` and its fields. */
  resolution?: ApprovalResolution | null;
  available_override_scopes?: ApprovalOverrideScope[];
  context: ApprovalContext;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

export interface SubmitTurnRequest {
  messages: Message[];
  metadata?: Record<string, unknown>;
  /** Client-minted turn id — 32 lowercase hex, dashless. Older backends ignore it. */
  turn_id?: string;
}

export interface SubmitTurnResponse {
  turn_id: string;
  /**
   * One artifact id per sent message, in order. Its presence is the identity
   * contract's feature-detect — see `hasIdentityEcho`.
   */
  user_message_ids?: string[];
}

/**
 * Why the client, not the user, cancelled a run. The literal union of the
 * backend's `ClientCancelReason` enum, so the two cannot drift. A user stop
 * sends none and the backend records `user_requested`.
 */
export type CancelTurnReason = `${ClientCancelReason}`;

export type CancelTurnResponse = void;

export type ApprovalVerdict = 'approve' | 'deny';

export interface ResolveApprovalRequest {
  verdict: ApprovalVerdict;
  modified_input?: Record<string, unknown> | null;
  reason?: string;
  /** Applied on `approve`. Absence means `once` (the BE default); only send an advertised scope. */
  override_scope?: ApprovalOverrideScope;
  /** With a verdict this approval accepts it with; the backend rejects any other with 422. */
  resolution?: ApprovalResolution;
}

export interface ResolveApprovalResponse {
  approval_id: string;
  verdict: ApprovalVerdict;
  accepted: boolean;
}

export interface AttachmentDownloadUrlResponse {
  url: string;
}

export type LLMStreamConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'streaming'
  | 'reconnecting'
  | 'done'
  | 'error';

export type LLMStreamEventType =
  | 'message_start'
  | 'text_delta'
  | 'thinking_delta'
  | 'tool_call_complete'
  | 'tool_call_start'
  | 'tool_input_delta'
  | 'message_end'
  | 'error'
  | 'tool_execution_start'
  | 'tool_execution_delta'
  | 'tool_execution_complete'
  | 'tool_execution_error'
  | 'approval_requested'
  | 'approval_resolved'
  | 'tool_result_blocks'
  | 'turn_started'
  | 'turn_completed'
  | 'turn_cancelled'
  | 'turn_failed'
  | 'prompt_context_failed'
  | 'remote.tool_request'
  | 'evidence.chunk';

export interface EvidenceChunkStreamPayload {
  parent_tool_call_id: string;
  subagent_run_id: string;
  seq: number;
  inner: Record<string, unknown>;
}

export interface LLMStreamEvent {
  type: LLMStreamEventType;
  text?: string;
  tool_call_id?: string;
  tool_name?: string;
  tool_input?: Record<string, unknown>;
  tool_input_merge?: ToolInputMerge | null;
  tool_result_blocks?: ToolResultBlock[];
  stop_reason?: string;
  usage?: Record<string, number> | null;
  provider_message_id?: string;
  model?: string;
  metadata: Record<string, unknown>;
  /** Top-level on the `approval_requested` payload (sibling to `approval_kind`), not in `metadata`. */
  available_override_scopes?: ApprovalOverrideScope[];
  evidence_chunk?: EvidenceChunkStreamPayload | null;
  // Identity fields, all absent on a backend without the identity contract.
  turn_id?: string;
  artifact_id?: string;
  message_index?: number;
  iteration?: number;
}

export interface LLMStreamEnvelope {
  id: string;
  event: LLMStreamEvent;
}

export interface TurnCancelledMetadata {
  turn_id: string;
  reason?: string | null;
  cancelled_at: string;
  input_state: 'ready';
  can_submit: true;
}

export interface ResolutionEnvelope {
  requires_approval: boolean;
  wait_for_ack: boolean;
  expires_at?: string | null;
}

export type KnownAgentEventType =
  | 'frontend.event'
  | 'remote.tool_request'
  | 'tool.approval_requested'
  | 'sub_agent.run_started'
  | 'run.title_generated'
  | 'turn.loader_phrases'
  | 'channel.ended';

export type AgentEventType = KnownAgentEventType | (string & {});

export type KnownUiEventType = 'ui.agent_demo' | 'ui.toast' | 'ui.modal';

export interface AgentEventBase {
  schema_version: 1;
  event_id: string;
  agent_run_id: string;
  turn_id?: string | null;
  issued_at: string;
  type: AgentEventType;
  resolution?: ResolutionEnvelope | null;
  payload?: unknown;
}

export interface FrontendEventAgentEvent extends AgentEventBase {
  type: 'frontend.event';
  payload: {
    event_type: string;
    data: Record<string, unknown>;
  };
  resolution?: ResolutionEnvelope | null;
}

export interface ChannelEndedAgentEvent extends AgentEventBase {
  type: 'channel.ended';
  payload?: {
    reason?: 'closed' | 'failed' | (string & {});
  };
}

export interface RunTitleGeneratedAgentEvent extends AgentEventBase {
  type: 'run.title_generated';
  payload: {
    title: string;
  };
}

export interface TurnLoaderPhrasesAgentEvent extends AgentEventBase {
  type: 'turn.loader_phrases';
  payload: {
    phrases: string[];
  };
}

export type AgentEvent =
  | FrontendEventAgentEvent
  | ChannelEndedAgentEvent
  | RunTitleGeneratedAgentEvent
  | TurnLoaderPhrasesAgentEvent
  | AgentEventBase;

export interface PendingApproval {
  approval_id: string;
  tool_call_id: string;
  tool_name: string;
  tool_input: Record<string, unknown>;
  tool_input_merge?: ToolInputMerge | null;
  /** Carried on the live SSE so a card can resolve `session` before the snapshot hydrates. */
  available_override_scopes?: ApprovalOverrideScope[];
  /** Backend-owned display, so an approval-only item (its tool_call block not yet
   * in the snapshot) still shows a human label instead of the raw tool name. */
  toolCallDisplay?: ToolCallDisplay;
  approval_kind?: string;
  context?: ApprovalContext;
}

export interface ChildAgentRunMetadata {
  childAgentRunId: string;
  childAgentName?: string;
  /**
   * Machine-readable child-agent classification (e.g. `'computer_use'`),
   * stamped from the child `AgentConfig.kind`. The BE omits it when unset, so
   * absence means "not a typed child" — discovery gates on the exact value.
   */
  kind?: string;
  /** Registered desktop target that owns a device-bound child, when pinned. */
  targetId?: string;
  childTurnId?: string | null;
  persistent?: boolean;
}

export interface StreamDiagnostics {
  usage: Record<string, number> | null;
  stop_reason: string | null;
  provider_message_id: string | null;
  model: string | null;
}

export interface PromptContextFailure {
  event_id: string;
  message: string;
  metadata?: Record<string, unknown>;
}

export type ToolUseStatus =
  | 'running'
  | 'complete'
  | 'error'
  | 'cancelled'
  | 'pending_approval'
  // Accumulated tool JSON is arriving; no approval id yet.
  | 'streaming_input'
  // Tool call received; execution state not yet observed.
  | 'queued';

/** Every field is optional so a backend without the identity contract still typechecks. */
export interface ChatItemIdentity {
  turnId?: string;
  iteration?: number;
  createdAt?: string;
  /** Live only — absent on a reloaded transcript. */
  seenAtMs?: { first: number; last: number };
}

export interface UserMessageItem extends ChatItemIdentity {
  kind: 'user_message';
  id: string;
  text: string;
  attachments: AttachmentBlock[];
  /**
   * Carried through from the wire ``Message.system_generated`` (AGE-2241).
   * True ONLY for the backend-synthesized routine-launch seed today — set
   * when ``SubmitTurnRequest.routine_name`` was present AND the run had no
   * prior turn (``project_service.py::submit_turn``). A real clinician
   * message can never carry this. Absent/undefined on a backend that
   * predates this marker, or on any other message — treat as falsy, never
   * as "definitely not a seed."
   */
  systemGenerated?: boolean | null;
}

export interface AssistantTextItem extends ChatItemIdentity {
  kind: 'assistant_text';
  id: string;
  text: string;
  streaming: boolean;
}

export interface ThinkingItem extends ChatItemIdentity {
  kind: 'thinking';
  id: string;
  text: string;
  streaming: boolean;
}

export interface ToolUseItem extends ChatItemIdentity {
  kind: 'tool_use';
  id: string;
  name: string;
  input: Record<string, unknown>;
  inputMerge?: ToolInputMerge | null;
  status: ToolUseStatus;
  /** Accumulated partial JSON while the model is still writing the tool input. */
  partialInput?: string;
  live_output?: string;
  result?: {
    content: unknown;
    is_error: boolean;
    attachments?: AttachmentBlock[];
  };
  approval?: ApprovalResponse;
  pendingApprovalId?: string;
  /** Override scopes for the live-only case where `approval` hasn't hydrated yet. */
  availableOverrideScopes?: ApprovalOverrideScope[];
  /** Live SSE approval kind before the snapshot hydrates ``approval``. */
  pendingApprovalKind?: string;
  /** Live SSE approval context (e.g. connect_required payload). */
  pendingApprovalContext?: Record<string, unknown>;
  toolCallDisplay?: ToolCallDisplay;
  provider_metadata?: Record<string, unknown>;
  result_provider_metadata?: Record<string, unknown>;
  childAgentRunId?: string;
  childAgentName?: string;
  childTurnId?: string | null;
  childPersistent?: boolean;
  isSubAgent?: boolean;
  isComputerUse: boolean;
  /**
   * Marks note-push **flow** rows — the hint tools (`get_note_push_hints` /
   * `record_note_push_hints`) that orchestrate a computer-use note push — NOT
   * the desktop delegation itself (that's `isComputerUse` on
   * `run_computer_use_task`). Stamped centrally like `isComputerUse` so
   * consumers don't each re-derive it from tool names and drift; the panel
   * consumers arrive with the note-push UX in the stacked PR.
   */
  isComputerUseNotePush: boolean;
  resultCreatedAt?: string;
  terminalOutcome?: ToolTerminalOutcome;
}

export interface PromptContextFailureItem {
  kind: 'prompt_context_failed';
  id: string;
  /** Server-provided detail; `null` when the renderer should show its fallback. */
  message: string | null;
}

export interface TurnCancelledMarkerItem extends ChatItemIdentity {
  kind: 'turn_cancelled_marker';
  id: string;
}

export interface TurnFailedItem extends ChatItemIdentity {
  kind: 'turn_failed';
  id: string;
  reason: string | null;
}

export type ChatItem =
  | UserMessageItem
  | AssistantTextItem
  | ThinkingItem
  | ToolUseItem
  | PromptContextFailureItem
  | TurnCancelledMarkerItem
  | TurnFailedItem;
